import { useEffect, useMemo, useState } from "react";
import { ComparisonPeriod, OrdersAnalyticsFilters, TrendGranularity } from "@/features/orders-analytics/types/orders-analytics.types";
import { timezone } from "@/features/orders-analytics/utils/timezone";

const parseList = (value: string | null): string[] =>
    value !== null && value !== ""
        ? value
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item !== "")
        : [];

const parseNumber = (value: string | null): number | undefined => {
    if (value === null || value === "") {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const allowedComparison: ComparisonPeriod[] = [
    "day",
    "week",
    "month",
    "month_to_date",
    "last_full_month",
    "year_to_date",
    "same_period_last_year",
    "previous_range",
];
const allowedGranularity: TrendGranularity[] = ["day", "week", "month"];

const parseEnum = <T extends string>(
    value: string | null,
    allowed: readonly T[],
    fallback: T
): T => {
    if (value === null || value === "") {
        return fallback;
    }

    const match = allowed.find((option) => option === value);
    return match ?? fallback;
};

const STORAGE_KEY = "vb-metrics.orders-analytics-search";

const readStoredSearch = (): string => {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY) ?? "";
    } catch {
        return "";
    }
};

const persistSearch = (search: string): void => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, search);
    } catch {
        // sessionStorage puede no estar disponible (modo privado); la URL sigue sincronizada
    }
};

/**
 * D114: the client selection is ONE fact shared by every page that shows the app's
 * filter bar. Daily reads and writes it here, through the same session entry this
 * hook persists, so picking a client anywhere applies everywhere. Only `clients` is
 * touched; the rest of the stored search is left as it was.
 */
export const ordersAnalyticsClientSelection = {
    read: (): string[] => parseList(new URLSearchParams(readStoredSearch()).get("clients")),
    write: (clients: string[]): void => {
        const params = new URLSearchParams(readStoredSearch());
        if (clients.length === 0) params.delete("clients");
        else params.set("clients", clients.join(","));
        persistSearch(params.toString());
    },
};

export const createDefaultOrdersAnalyticsFilters = (): OrdersAnalyticsFilters => {
    const now = Date.now();
    const monthStart = timezone.getTimeZoneMonthStartTimestamp(now);

    return {
        dateFrom: monthStart,
        dateTo: now,
        comparisonPeriod: "month_to_date",
        granularity: "day",
        operationType: [],
        status: [],
        pair: [],
        destinationRail: [],
        withdrawProvider: [],
        depositType: [],
        onrampProvider: [],
        referral: "all",
        amountMin: undefined,
        amountMax: undefined,
        country: [],
        userType: "all",
        clients: [],
    };
};

const readFromSearch = (): OrdersAnalyticsFilters => {
    const defaults = createDefaultOrdersAnalyticsFilters();
    const search = window.location.search !== "" ? window.location.search : readStoredSearch();
    const params = new URLSearchParams(search);

    return {
        dateFrom: parseNumber(params.get("dateFrom")) ?? defaults.dateFrom,
        dateTo: parseNumber(params.get("dateTo")) ?? defaults.dateTo,
        comparisonPeriod: parseEnum(
            params.get("comparisonPeriod"),
            allowedComparison,
            defaults.comparisonPeriod
        ),
        granularity: parseEnum(
            params.get("granularity"),
            allowedGranularity,
            defaults.granularity
        ),
        operationType: [],
        status: [],
        pair: [],
        destinationRail: [],
        withdrawProvider: [],
        depositType: [],
        onrampProvider: [],
        referral: defaults.referral,
        amountMin: undefined,
        amountMax: undefined,
        country: [],
        userType: defaults.userType,
        clients: parseList(params.get("clients")),
    };
};

const buildSearch = (filters: OrdersAnalyticsFilters) => {
    const params = new URLSearchParams();

    params.set("dateFrom", String(filters.dateFrom));
    params.set("dateTo", String(filters.dateTo));
    params.set("comparisonPeriod", filters.comparisonPeriod);
    params.set("granularity", filters.granularity);

    const setList = (key: string, values: string[]) => {
        if (values.length > 0) {
            params.set(key, values.join(","));
        }
    };

    setList("clients", filters.clients);

    return params.toString();
};

export function useOrdersAnalyticsFilters() {
    const [filters, setFilters] = useState<OrdersAnalyticsFilters>(() => readFromSearch());

    useEffect(() => {
        const search = buildSearch(filters);
        const nextUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname;
        window.history.replaceState({}, "", nextUrl);
        persistSearch(search);
    }, [filters]);

    const actions = useMemo(
        () => ({
            setFilters,
            reset: () => setFilters(createDefaultOrdersAnalyticsFilters()),
        }),
        []
    );

    return {
        filters,
        ...actions,
    };
}
